import React from 'react';
import { CircleDollarSign, Users, Lightbulb, PhoneCall } from 'lucide-react';

const AboutSection = () => {
  return (
    <div className="py-16 px-4 bg-brand-dark text-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 font-serif">
            Who We Are
          </h2>
          <p className="text-xl max-w-3xl mx-auto text-gray-300">
            A Christ-centered digital ministry helping people through technology
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-black/40 backdrop-blur-sm rounded-lg p-8 border border-coral-900/50 shadow-lg">
            <h3 className="text-2xl font-semibold mb-4 font-serif text-white">
              Our Mission
            </h3>
            <p className="text-gray-300">
              We help others through tech,
              fostering economic growth and digital literacy in our communities. 
              Our approach transforms technical expertise into accessible tools for 
              business development and community flourishing.
            </p>
          </div>

          <div className="bg-black/40 backdrop-blur-sm rounded-lg p-8 border border-coral-900/50 shadow-lg">
            <h3 className="text-2xl font-semibold mb-4 font-serif text-white">
              Our Vision
            </h3>
            <p className="text-gray-300">
              We envision a world where every person, regardless of resources,
              can fully participate in the digital economy while being supported by 
              a Christ-centered community of technology professionals.
            </p>
          </div>
        </div>

        {/* Programs & Services Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 font-serif">
            Overview of Our Services
          </h2>
          <p className="text-gray-300">This is tentative and subject to change</p>
        </div>

        {/* Main Content Sections */}
        <div className="space-y-12">
          {/* Sliding Scale Services */}
          <div className="bg-black/40 backdrop-blur-sm rounded-lg p-8 border border-coral-900/50 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="text-coral-400 flex-shrink-0 mt-1">
                <CircleDollarSign className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3 font-serif text-white">
                  Sliding Scale Services
                </h3>
                <p className="text-gray-300 mb-4">
                  We believe every business deserves access to professional technology solutions. 
                  Our sliding scale pricing is based on your organization's size and resources, 
                  ensuring our services remain accessible to those who need them most.
                </p>
                <div className="bg-black/30 rounded-lg p-4">
                  <p className="text-sm text-gray-300">
                    Service rates typically range from $0 - $150/hour based on:
                  </p>
                  <ul className="mt-2 space-y-2 text-sm text-gray-300">
                    <li>• Annual revenue and business size</li>
                    <li>• Non-profit/ministry status</li>
                    <li>• Community impact and mission alignment</li>
                    <li>• Resource availability and technical needs</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Ideal Partnerships */}
          <div className="bg-black/40 backdrop-blur-sm rounded-lg p-8 border border-coral-900/50 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="text-coral-400 flex-shrink-0 mt-1">
                <Users className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3 font-serif text-white">
                  Ideal Partnerships
                </h3>
                <p className="text-gray-300 mb-4">
                  We work best with organizations that:
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-coral-400">•</span>
                      Are small businesses or non-profits seeking to expand their digital presence
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-coral-400">•</span>
                      Need help with website development, digital tools, or technology training
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-coral-400">•</span>
                      Want to improve their operational efficiency through technology
                    </li>
                  </ul>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-coral-400">•</span>
                      Are committed to serving their local community
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-coral-400">•</span>
                      Value long-term partnership and sustainable growth
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-coral-400">•</span>
                      Share our vision for community development
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-coral-400">•</span>
                      Underserved persons or groups
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Get Started */}
          <div className="bg-black/40 backdrop-blur-sm rounded-lg p-8 border border-coral-900/50 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="text-coral-400 flex-shrink-0 mt-1">
                <PhoneCall className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3 font-serif text-white">
                  How to Get Started
                </h3>
                <p className="text-gray-300 mb-4">
                  Getting help with your technology needs is simple:
                </p>
                <ol className="space-y-4 text-gray-300">
                  <li className="flex gap-4">
                    <span className="font-bold text-coral-400">1.</span>
                    <p>Fill out our contact form below with details about your organization and needs</p>
                  </li>
                  <li className="flex gap-4">
                    <span className="font-bold text-coral-400">2.</span>
                    <p>Schedule a free consultation to discuss your goals and explore solutions</p>
                  </li>
                  <li className="flex gap-4">
                    <span className="font-bold text-coral-400">3.</span>
                    <p>Receive a customized proposal including sliding scale pricing options</p>
                  </li>
                  <li className="flex gap-4">
                    <span className="font-bold text-coral-400">4.</span>
                    <p>Begin your journey toward digital transformation with our support</p>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutSection;